import React, { useState } from 'react'
import Modal, { ModalProps } from 'components/modal'
import Button from 'components/button'
import { useApplicationStore } from 'hooks/application'
import { Link, generatePath } from 'react-router-dom'
import { useDataContext } from 'components/document/contexts/data'
import Toggle from 'components/toggle'
import Checkbox from 'components/checkbox'
import Select from 'components/select'
import useProject from 'hooks/useProject'
import { PROJECT } from 'app-constants'
import { Chart } from 'chart.js'
import { MimeType, TemplateData } from 'easy-template-x'
import {
  addWhiteBackground,
  drawCardChartTitle,
  getChartDimensions,
  waitForPlaceholdersToMatch,
  extractPlaceholdersFromDocx,
  extractIframePlaceholdersFromDocx,
  extractIframesFromDocument,
  matchIframePlaceholders,
  prepareIframeTemplateData,
  downloadBlob,
  fillExportPlaceholders
} from './helpers'
import { CombinedPlaceholders, IframeWithPlaceholder, IframeCaptureResult, ViewGrids } from './types'
import { buildViewGrids } from './viewGrids'
import { SETTINGS } from 'routes'
import IframeCapture from './IframeCapture'
import { captureCustomViews } from './customViews'
import api from 'helpers/api'

type ExportStep = 'options' | 'capturing' | 'preview' | 'processing' | 'complete'

interface IframeCaptureState {
  iframes: IframeWithPlaceholder[]
  captures: Record<string, IframeCaptureResult>
  progress: { current: number; total: number }
  captureSessionId: number
}

const ExportModal: React.FC<ModalProps> = ({ id, open, setOpen, onClose }) => {
  const { project, projectOwner } = useProject()
  const { process, setProcess } = useDataContext()
  const { displayErrorMessage, setSnackbarMessage, clearSnackbar } = useApplicationStore()
  const [tablesAsLinks, setTablesAsLinks] = useState<boolean>(false)
  const [iframesAsLinks, setIframesAsLinks] = useState<boolean>(false)
  const [defaultFormatAndOrientation, setDefaultFormatAndOrientation] = useState<boolean>(false)
  const [pageFormat, setPageFormat] = useState<string>(process?.defaultFormat || 'A4')
  const [pageOrientation, setPageOrientation] = useState<string>(process?.defaultOrientation || 'portrait')
  const [step, setStep] = useState<ExportStep>('options')
  const [docxBuffer, setDocxBuffer] = useState<ArrayBuffer | null>(null)
  const [chartTemplateData, setChartTemplateData] = useState<TemplateData>({})
  const [iframeCaptureState, setIframeCaptureState] = useState<IframeCaptureState>({
    iframes: [],
    captures: {},
    progress: { current: 0, total: 0 },
    captureSessionId: 0
  })

  const resetModal = () => {
    setStep('options')
    setDocxBuffer(null)
    setChartTemplateData({})
    setIframeCaptureState({
      iframes: [],
      captures: {},
      progress: { current: 0, total: 0 },
      captureSessionId: 0
    })
  }

  const handleClose = () => {
    resetModal()
    setOpen(false)
    if (onClose) onClose()
  }

  const captureCanvasWithCharts = async (documentPlaceholders: CombinedPlaceholders): Promise<TemplateData> => {
    const templateData: TemplateData = {}

    for (const {
      canvas,
      element,
      tableId,
      viewId,
      height: instanceHeight,
      columns,
      instance
    } of documentPlaceholders) {
      if (!tableId || !viewId || !instanceHeight || !columns || !instance || columns <= 0) {
        continue
      }

      const placeholderKey = `CHART tableId=${tableId} viewId=${viewId} height=${instanceHeight} columns=${columns} instance=${instance}`

      // Card chart (no canvas): render cards directly with Canvas 2D API
      // Reads actual DOM dimensions to match the on-screen layout exactly
      if (!canvas && element) {
        const renderCardChart = async (): Promise<{
          arrayBuffer: ArrayBuffer
          width: number
          height: number
        } | null> => {
          const container = element as HTMLElement
          const cardTiles = container.querySelectorAll('[data-card-tile]')
          if (cardTiles.length === 0) return null

          // Read actual DOM dimensions
          const domW = container.offsetWidth
          const domH = container.offsetHeight
          if (!domW || !domH) return null
          const domAspect = domW / domH

          // Compute export size preserving the DOM aspect ratio
          const { width: exportW, scale } = getChartDimensions(pageFormat, pageOrientation, undefined, columns)
          const exportH = Math.round(exportW / domAspect)
          const S = scale
          const scaleFactor = (exportW * S) / domW // DOM px → canvas px

          const canvasEl = document.createElement('canvas')
          canvasEl.width = exportW * S
          canvasEl.height = exportH * S
          const ctx = canvasEl.getContext('2d')
          if (!ctx) return null

          // White background
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, canvasEl.width, canvasEl.height)

          // Read each card's actual position, size, text, and colours from the DOM
          const containerRect = container.getBoundingClientRect()

          // View-name heading (Show Title). Canvas-based charts get theirs from Chart.js;
          // a card chart draws its own DOM heading, so replay it at the same position.
          drawCardChartTitle(ctx, container, containerRect, canvasEl.width, scaleFactor)

          cardTiles.forEach((tile) => {
            const el = tile as HTMLElement
            const tileRect = el.getBoundingClientRect()

            // Position relative to container, scaled to canvas
            const x = (tileRect.left - containerRect.left) * scaleFactor
            const y = (tileRect.top - containerRect.top) * scaleFactor
            const w = tileRect.width * scaleFactor
            const h = tileRect.height * scaleFactor
            const radius = 8 * scaleFactor

            // Card background
            const bg = getComputedStyle(el).backgroundColor || '#F5F5F5'
            ctx.fillStyle = bg
            ctx.shadowColor = 'rgba(0,0,0,0.1)'
            ctx.shadowBlur = 4 * scaleFactor
            ctx.shadowOffsetY = 2 * scaleFactor
            ctx.beginPath()
            ctx.roundRect(x, y, w, h, radius)
            ctx.fill()
            ctx.shadowColor = 'transparent'
            ctx.shadowBlur = 0
            ctx.shadowOffsetY = 0

            const fg = getComputedStyle(el).color || '#1a1a2e'
            ctx.fillStyle = fg
            ctx.textAlign = 'center'

            // Value text (read computed font size from DOM)
            const valueEl = el.querySelector('.text-4xl') as HTMLElement | null
            if (valueEl) {
              const domFontSize = parseFloat(getComputedStyle(valueEl).fontSize)
              const fontSize = Math.round(domFontSize * scaleFactor)
              ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
              ctx.textBaseline = 'middle'
              const valRect = valueEl.getBoundingClientRect()
              const valCenterY = (valRect.top - containerRect.top + valRect.height / 2) * scaleFactor
              const valText = valueEl.textContent ? valueEl.textContent.trim() : ''
              ctx.fillText(valText, x + w / 2, valCenterY, w - 16 * scaleFactor)
            }

            // Label and percentage text
            const textEls = el.querySelectorAll('.text-sm')
            textEls.forEach((textNode) => {
              const textEl = textNode as HTMLElement
              const domFontSize = parseFloat(getComputedStyle(textEl).fontSize)
              const fontSize = Math.round(domFontSize * scaleFactor)
              const opacity = parseFloat(getComputedStyle(textEl).opacity) || 1
              ctx.globalAlpha = opacity
              ctx.font = `500 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
              ctx.textBaseline = 'middle'
              const textRect = textEl.getBoundingClientRect()
              const textCenterY = (textRect.top - containerRect.top + textRect.height / 2) * scaleFactor
              const labelText = textEl.textContent ? textEl.textContent.trim() : ''
              ctx.fillText(labelText, x + w / 2, textCenterY, w - 16 * scaleFactor)
              ctx.globalAlpha = 1
            })
          })

          const blob = await new Promise<Blob>((resolve, reject) =>
            canvasEl.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png')
          )
          const arrayBuffer = await blob.arrayBuffer()
          return { arrayBuffer, width: exportW, height: exportH }
        }

        try {
          const result = await renderCardChart()
          if (result) {
            templateData[placeholderKey] = {
              _type: 'image',
              source: result.arrayBuffer,
              format: MimeType.Png,
              width: result.width,
              height: result.height
            }
          }
        } catch {
          // skip
        }
        continue
      }

      // Chart.js canvas chart: existing flow
      if (!canvas) continue
      const originalChartInstance = Chart.getChart(canvas)

      if (!originalChartInstance) {
        continue
      }

      // Render Chart.js chart to an offscreen canvas for export
      const renderChartJsChart = async (): Promise<{
        arrayBuffer: ArrayBuffer
        width: number
        height: number
        chartInstance: Chart
      } | null> => {
        const REFERENCE_WIDTH = pageOrientation === 'portrait' ? 1000 : 1414
        const chartAspectRatio = REFERENCE_WIDTH / originalChartInstance.height
        const { width, height, scale } = getChartDimensions(pageFormat, pageOrientation, chartAspectRatio, columns)

        let newCanvas = new OffscreenCanvas(width * scale, height * scale)
        const newCtx = newCanvas.getContext('2d')

        if (!newCtx) return null

        const originalConfig = originalChartInstance.config as {
          type: string
          options?: Record<string, unknown> & { plugins?: Record<string, unknown> }
          plugins?: any[]
        }
        const originalOptions = originalConfig.options || {}
        const originalPlugins = originalOptions.plugins || {}

        const safePluginKeys = ['title', 'subtitle', 'legend', 'datalabels', 'annotation']
        const safePlugins: Record<string, unknown> = {
          zoom: false
        }
        for (const key of safePluginKeys) {
          if (originalPlugins[key] !== undefined) {
            safePlugins[key] = originalPlugins[key]
          }
        }

        // Remove the "today" indicator from Gantt chart exports
        const annotPlugin = safePlugins.annotation as { annotations?: Record<string, unknown> } | undefined
        if (annotPlugin && annotPlugin.annotations && annotPlugin.annotations.todayLine) {
          delete annotPlugin.annotations.todayLine
        }

        const exportConfig = {
          type: originalConfig.type,
          data: originalChartInstance.data,
          options: {
            ...originalOptions,
            animation: false as const,
            responsive: false,
            events: [] as [],
            plugins: safePlugins
          },
          plugins: originalConfig.plugins || []
        }

        const chartInstance = new Chart(
          newCanvas as unknown as HTMLCanvasElement,
          exportConfig as typeof originalChartInstance.config
        )
        newCanvas = addWhiteBackground(newCanvas)

        const blob = await (
          newCanvas as unknown as { convertToBlob: (options: { type: string }) => Promise<Blob> }
        ).convertToBlob({
          type: 'image/png'
        })
        const arrayBuffer = await blob.arrayBuffer()
        return { arrayBuffer, width, height, chartInstance }
      }

      let newChartInstance: Chart | null = null
      try {
        const result = await renderChartJsChart()
        if (result) {
          newChartInstance = result.chartInstance
          templateData[placeholderKey] = {
            _type: 'image',
            source: result.arrayBuffer,
            format: MimeType.Png,
            width: result.width,
            height: result.height
          }
        }
      } catch {
        // Skip error-prone charts without crashing export
      }
      // Always destroy the temporary chart instance to free memory
      if (newChartInstance) {
        newChartInstance.destroy()
      }
    }

    return templateData
  }

  const finalizeExport = async (buffer: ArrayBuffer, chartData: TemplateData, iframeData: TemplateData) => {
    const processedDocx = await fillExportPlaceholders(buffer, { ...chartData, ...iframeData })
    const processedBlob = new Blob([processedDocx], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    })

    const filename = `${process!.name}.docx`
    downloadBlob(processedBlob, filename)

    setStep('complete')
    handleClose()
  }

  const handleIframeCaptureComplete = async (captures: Record<string, IframeCaptureResult>) => {
    clearSnackbar()
    setIframeCaptureState((prev) => ({ ...prev, captures }))
    setStep('preview')
  }

  const handleIframeCaptureProgress = (current: number, total: number) => {
    setIframeCaptureState((prev) => ({
      ...prev,
      progress: { current, total }
    }))
    showCaptureProgress('iframe')(current, total)
  }

  // Shows capture progress in the snackbar (current is 0-indexed, and the final call is current === total).
  const showCaptureProgress = (label: string) => (current: number, total: number) => {
    if (current < total) {
      setSnackbarMessage({
        status: 'warning',
        message: `Exporting word document: capturing ${label} ${current + 1} of ${total}`,
        duration: 10000
      })
    }
  }

  const handleRetryCapture = (placeholderKey: string) => {
    const iframe = iframeCaptureState.iframes.find((i) => i.placeholderKey === placeholderKey)
    if (!iframe) return

    setIframeCaptureState((prev) => {
      const newCaptures = { ...prev.captures }
      delete newCaptures[placeholderKey]
      return {
        ...prev,
        captures: newCaptures,
        iframes: [iframe],
        captureSessionId: prev.captureSessionId + 1
      }
    })
    setStep('capturing')
  }

  const handleRetryAllFailed = () => {
    const failedIframes = iframeCaptureState.iframes.filter(
      (iframe) => iframeCaptureState.captures[iframe.placeholderKey]?.status === 'failed'
    )

    if (failedIframes.length === 0) return

    setIframeCaptureState((prev) => {
      const newCaptures = { ...prev.captures }
      for (const iframe of failedIframes) {
        delete newCaptures[iframe.placeholderKey]
      }
      return {
        ...prev,
        captures: newCaptures,
        iframes: failedIframes,
        captureSessionId: prev.captureSessionId + 1
      }
    })
    setStep('capturing')
  }

  const handlePreviewContinue = async () => {
    if (!docxBuffer) return

    setStep('processing')

    try {
      const iframeTemplateData = await prepareIframeTemplateData(
        iframeCaptureState.captures,
        pageFormat,
        pageOrientation
      )
      await finalizeExport(docxBuffer, chartTemplateData, iframeTemplateData)
    } catch (error) {
      displayErrorMessage(error)
      handleClose()
    }
  }

  const exportProcess = async () => {
    if (!process) return

    setStep('processing')

    try {
      // Compute matrix/transposed view layouts on the frontend (the pivot is only done here) and
      // pass them so the backend renders those views in the correct arrangement. Skipped when
      // exporting tables as links, where the views aren't rendered as tables at all.
      let viewGrids: ViewGrids = {}
      if (!tablesAsLinks) {
        viewGrids = await buildViewGrids(process, process.publicId)
      }

      const buffer = await api.exportDocument(
        process.publicId,
        tablesAsLinks,
        iframesAsLinks,
        pageFormat,
        pageOrientation,
        defaultFormatAndOrientation,
        viewGrids
      )

      if (!buffer) {
        setSnackbarMessage({
          status: 'error',
          message: "The document couldn't be downloaded. Try again.",
          offerSupport: true
        })
        handleClose()
        return
      }

      if (defaultFormatAndOrientation) {
        setProcess({
          ...process,
          defaultFormat: pageFormat,
          defaultOrientation: pageOrientation
        })
      }

      setDocxBuffer(buffer)

      const docxChartPlaceholders = await extractPlaceholdersFromDocx(buffer)
      const customViewPlaceholders = await extractPlaceholdersFromDocx(buffer, 'CUSTOM_VIEW')

      // Force all lazy-loaded embedded tables/dashboards to render so their
      // chart canvases and Custom View pages are in the DOM for capture.
      if (docxChartPlaceholders.length + customViewPlaceholders.length > 0) {
        window.dispatchEvent(new Event('force-render-embeds'))
      }

      const documentPlaceholders = await waitForPlaceholdersToMatch(docxChartPlaceholders)
      const chartData = {
        ...(await captureCanvasWithCharts(documentPlaceholders)),
        ...(await captureCustomViews(
          customViewPlaceholders,
          pageFormat,
          pageOrientation,
          showCaptureProgress('Custom View')
        ))
      }
      setChartTemplateData(chartData)

      if (!iframesAsLinks) {
        const docxIframePlaceholders = await extractIframePlaceholdersFromDocx(buffer)

        if (docxIframePlaceholders.length > 0) {
          const documentIframes = extractIframesFromDocument(process)

          const matchedIframes = matchIframePlaceholders(docxIframePlaceholders, documentIframes)

          if (matchedIframes.length > 0) {
            setIframeCaptureState((prev) => ({
              iframes: matchedIframes,
              captures: {},
              progress: { current: 0, total: matchedIframes.length },
              captureSessionId: prev.captureSessionId + 1
            }))
            setStep('capturing')
            return
          }
        }
      }

      await finalizeExport(buffer, chartData, {})
    } catch (error) {
      displayErrorMessage(error)
      handleClose()
    }
  }

  const hasFailedCaptures = Object.values(iframeCaptureState.captures).some((c) => c.status === 'failed')
  const allCapturesFailed = Object.values(iframeCaptureState.captures).every((c) => c.status === 'failed')
  const successfulCaptures = Object.values(iframeCaptureState.captures).filter((c) => c.status === 'success').length

  const renderOptionsStep = () => (
    <>
      {!project.loading && !project.wordTemplate && (
        <div className="mb-8 flex flex-col bg-light-yellow rounded-lg p-5">
          <div className="mb-4">
            This export will use Morta&apos;s default format. To export to a different format, ask your {PROJECT}{' '}
            administrator to head to
            <Link
              className="ml-[5px] mr-[5px]"
              to={generatePath(SETTINGS, { projectId: project.publicId }) + '?tab=styling'}
            >
              Style Settings
            </Link>{' '}
            and upload a Word Template.
          </div>
        </div>
      )}

      <div className="mb-8 flex items-center h-[45px]">
        <div className="w-[60%]">Page Format</div>
        <div className="w-full">
          <Select
            options={[
              { value: 'A2', label: 'A2' },
              { value: 'A3', label: 'A3' },
              { value: 'A4', label: 'A4' },
              { value: 'letter', label: 'Letter' },
              { value: 'legal', label: 'Legal' }
            ]}
            optionsSelected={[pageFormat]}
            onOptionClick={(option) => setPageFormat(option)}
            searchable={false}
          />
        </div>
      </div>
      <div className="mb-8 flex items-center h-[45px]">
        <div className="w-[60%]">Page Orientation</div>
        <div className="w-full">
          <Select
            options={[
              { value: 'portrait', label: 'Portrait' },
              { value: 'landscape', label: 'Landscape' }
            ]}
            optionsSelected={[pageOrientation]}
            onOptionClick={(option) => setPageOrientation(option)}
            searchable={false}
          />
        </div>
      </div>

      {projectOwner && (
        <div className="mb-8 flex items-center h-[45px]">
          <div className="w-[60%]">Set Format And Orientation As Default</div>
          <div className="w-full">
            <Checkbox
              checked={defaultFormatAndOrientation}
              onChange={() => setDefaultFormatAndOrientation(!defaultFormatAndOrientation)}
            />
          </div>
        </div>
      )}

      <div className="mb-8 flex items-center h-[45px]">
        <div className="w-[60%]">Export All Tables As Links</div>
        <div className="w-full">
          <Toggle
            aria-label="Export All Tables As Links"
            toggled={tablesAsLinks}
            onToggle={(toggled: boolean) => setTablesAsLinks(toggled)}
          />
        </div>
      </div>

      <div className="mb-8 flex items-center h-[45px]">
        <div className="w-[60%]">Export All Iframes As Links</div>
        <div className="w-full">
          <Toggle
            aria-label="Export All Iframes As Links"
            toggled={iframesAsLinks}
            onToggle={(toggled: boolean) => setIframesAsLinks(toggled)}
          />
        </div>
      </div>

      <div className="mb-8 flex items-center justify-end h-[45px]">
        <Button className="text-base" internalType="danger" onClick={handleClose}>
          Cancel
        </Button>
        <Button className="text-base ml-[15px]" internalType="accept" onClick={() => exportProcess()}>
          Export
        </Button>
      </div>
    </>
  )

  const renderPreviewStep = () => (
    <>
      <div className="mb-4 font-medium text-[18px]">Iframe Preview</div>
      <div className="mb-4 text-muted">
        {successfulCaptures} of {Object.keys(iframeCaptureState.captures).length} iframes captured successfully
      </div>

      <div className="mb-8 grid overflow-y-auto gap-4 p-2 max-h-[300px] grid-cols-[repeat(auto-fill,minmax(150px,1fr))]">
        {Object.entries(iframeCaptureState.captures).map(([key, capture]) => (
          <div key={key} className="text-center p-2 rounded-lg border border-divider">
            {capture.status === 'success' ? (
              <>
                <img
                  src={URL.createObjectURL(capture.blob)}
                  alt="Captured iframe w-full"
                  className="object-cover mb-2 h-20 rounded"
                />
                <div className="text-green text-[14px]">Captured</div>
              </>
            ) : (
              <>
                <div className="flex justify-center items-center w-full mb-2 h-20 bg-really-light-red rounded text-red">
                  Failed
                </div>
                <div className="mb-2 text-red text-[12px]">{capture.error}</div>
                <Button internalType="outline" onClick={() => handleRetryCapture(key)} className="text-[12px]">
                  Retry
                </Button>
              </>
            )}
          </div>
        ))}
      </div>

      {allCapturesFailed && (
        <div className="mb-8 p-4 bg-really-light-red rounded-lg text-red">
          All iframes failed to capture. Please ensure you&apos;re logged in to external services and try again.
        </div>
      )}

      <div className="flex items-center justify-between h-[45px]">
        <div>
          {hasFailedCaptures && !allCapturesFailed && (
            <Button internalType="outline" onClick={handleRetryAllFailed}>
              Retry All Failed
            </Button>
          )}
        </div>
        <div className="flex items-center">
          <Button internalType="danger" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="ml-[15px]"
            internalType="accept"
            onClick={handlePreviewContinue}
            disabled={allCapturesFailed}
          >
            Continue
          </Button>
        </div>
      </div>
    </>
  )

  const renderProcessingStep = () => (
    <div className="flex flex-col items-center justify-center p-10">
      <div className="mb-4 font-medium text-[18px]">Generating Document</div>
      <div className="text-muted">Please wait...</div>
    </div>
  )

  // During capturing step, the modal is hidden but the IframeCapture component continues to run
  const isCapturing = step === 'capturing'

  return (
    <>
      {/* IframeCapture component - key ensures fresh instance on each capture session */}
      {isCapturing && iframeCaptureState.iframes.length > 0 && (
        <IframeCapture
          key={`capture-session-${iframeCaptureState.captureSessionId}`}
          iframes={iframeCaptureState.iframes}
          onCaptureComplete={handleIframeCaptureComplete}
          onProgress={handleIframeCaptureProgress}
        />
      )}

      {/* Modal - hidden with CSS during capture step to avoid unmounting issues */}
      <div>
        <Modal
          id={id}
          open={open}
          setOpen={setOpen}
          onClose={handleClose}
          title="Export Word Document"
          style={isCapturing ? { visibility: 'hidden', position: 'absolute', pointerEvents: 'none' } : {}}
        >
          {step === 'options' && renderOptionsStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'processing' && renderProcessingStep()}
        </Modal>
      </div>
    </>
  )
}

export default ExportModal
